"use client";

import {
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { CMSData } from "@/cms/models";
import {
  applyHeaderPresentation,
  resolveSharedHeader,
  type HeaderConfig,
} from "@/cms/shared-headers";
import { responsiveValue } from "@/utils/visual-style";
import { resolveFloatingHeaderSettings } from "@/utils/visual-defaults";
import { HeaderDesignContext } from "./HeaderDesignContext";
import DefaultHeader from "./default/DefaultHeader";
import TransparentHeader from "./transparent/TransparentHeader";
import FloatingHeader from "./floating/FloatingHeader";
import ClassicHeader from "./classic/ClassicHeader";
import HeaderStateController from "./HeaderStateController";
import { lookHeaderSettings } from "@/utils/header-state";
import { withLookHeights } from "@/utils/header-look";
import {
  headerColourVariables,
  headerTextOverPage,
} from "@/utils/header-colours";

const types = {
  DefaultHeader,
  TransparentHeader,
  FloatingHeader,
  ClassicHeader,
};
const query = "(min-width: 768px)";
const subscribe = (notify: () => void) => {
  const media = window.matchMedia(query);
  media.addEventListener("change", notify);
  return () => media.removeEventListener("change", notify);
};
const desktopSnapshot = () => window.matchMedia(query).matches;
const serverSnapshot = () => false;

/** Mounted only for explicit new header settings. The legacy DOM is unchanged. */
export default function SharedHeaderLayout({
  cmsData,
  pageHeader,
  appData,
  children,
}: {
  cmsData: CMSData;
  pageHeader?: HeaderConfig;
  appData: any;
  children: ReactNode;
}) {
  const desktop = useSyncExternalStore(
    subscribe,
    desktopSnapshot,
    serverSnapshot,
  );
  const resolved = resolveSharedHeader(cmsData, pageHeader);
  const merged = {
    ...resolved.config,
    classic: { ...resolved.base.classic, ...resolved.config.classic },
  };
  const presented = applyHeaderPresentation(
    merged,
    resolved.presentation,
    desktop,
  );
  const headerSettings = lookHeaderSettings(
    cmsData.theme,
    pageHeader,
    presented,
  );
  const header = withLookHeights(presented, headerSettings);
  const definition = resolved.definition;
  const headerData = {
    ...cmsData,
    layout: { ...cmsData.layout, header },
    ...(definition?.logoUrl ? { logoUrl: definition.logoUrl } : {}),
    ...(definition?.inverseLogoUrl
      ? { inverseLogoUrl: definition.inverseLogoUrl }
      : {}),
  } as CMSData;
  const app = {
    ...appData,
    ...(definition?.logoUrl
      ? { logo: { ...appData?.logo, originalUrl: definition.logoUrl } }
      : {}),
    ...(definition?.inverseLogoUrl
      ? {
          inverseLogo: {
            ...appData?.inverseLogo,
            originalUrl: definition.inverseLogoUrl,
          },
        }
      : {}),
  };
  const Header = types[header.type as keyof typeof types] ?? DefaultHeader;
  const floating = header.type === "FloatingHeader";
  const options = resolveFloatingHeaderSettings(header.floating);
  const fullWidth =
    options.geometry === "fullwidth" ||
    (cmsData.theme.appearance?.defaultShape === "angular" &&
      options.angularFullWidth);
  const occupied = (wide: boolean) =>
    fullWidth ? "0px" : responsiveValue(options.top, wide);
  const bar = (wide: boolean) =>
    options.geometry === "fullwidth"
      ? `max(76px, ${responsiveValue(options.height, wide)})`
      : responsiveValue(options.height, wide);
  const colours = headerColourVariables(
    cmsData.theme,
    definition?.colours ?? {},
    header.type,
  ) as CSSProperties;
  const heights = {
    "--heightMobile": header.heightMobile || "80px",
    "--heightDesktop":
      header.heightDesktop ||
      (header.type === "DefaultHeader" ? "100px" : "80px"),
    "--heightMobileVisible": header.heightMobileVisible || "60px",
    "--heightDesktopVisible":
      header.heightDesktopVisible || header.heightMobileVisible || "60px",
    ...(floating
      ? {
          "--re-header-occupied": `calc(${occupied(false)} + ${bar(false)} + 24px)`,
          "--re-header-occupied-desktop": `calc(${occupied(true)} + ${bar(true)} + 24px)`,
        }
      : {}),
  } as CSSProperties;
  const offset = floating
    ? header.contentPlacement !== "overlay"
      ? "re-page-offset"
      : ""
    : header.type === "DefaultHeader" ||
        (header.type === "TransparentHeader" &&
          (headerSettings
            ? headerSettings.top === "solid"
            : header.isVisible)) ||
        (header.type === "ClassicHeader" &&
          header.isVisible &&
          !header.presentationMode)
      ? "pt-[var(--heightMobile)] md:pt-[var(--heightDesktop)]"
      : "";
  const sharedHeader = (
    <div
      data-shared-header={definition?.id ?? "presentation"}
      style={{
        ...colours,
        ...headerTextOverPage(
          cmsData.theme,
          definition?.colours ?? {},
          header.type,
        ),
        display: "contents",
      }}
    >
      <Header
        logoDefinition={definition}
        header={header}
        cmsData={headerData}
        appData={app}
        pageHeader={{}}
        isVisible={true}
      />
    </div>
  );
  return (
    <HeaderDesignContext.Provider value={colours}>
      {floating && (
        <style>{`html { scroll-padding-top: calc(${occupied(false)} + ${bar(false)} + 24px); } @media (min-width:768px) { html { scroll-padding-top: calc(${occupied(true)} + ${bar(true)} + 24px); } }`}</style>
      )}
      {headerSettings ? (
        <HeaderStateController settings={headerSettings}>
          {sharedHeader}
        </HeaderStateController>
      ) : (
        sharedHeader
      )}
      <div
        data-header-content
        style={heights}
        className={`flex flex-col ${offset}`}
      >
        {children}
      </div>
    </HeaderDesignContext.Provider>
  );
}
